import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

/**
 * Update banner / overlay for native app version checks.
 *
 * - forceUpdate=true  → full-screen blocking overlay (like MaintenanceOverlay)
 * - forceUpdate=false → dismissible top-of-screen banner
 */
function UpdateBanner({ updateAvailable, forceUpdate, latestVersion, updateMessage, storeUrl, onDismiss }) {
  const { t } = useTranslation();

  if (!updateAvailable && !forceUpdate) return null;

  const handleUpdate = () => {
    if (storeUrl) {
      window.open(storeUrl, '_system');
    }
  };

  // Force update → blocking overlay
  if (forceUpdate) {
    const overlay = (
      <div style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        background: 'rgba(15, 23, 42, 0.85)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        animation: 'updateFadeIn 400ms ease-out',
      }}>
        <div style={{
          width: '100%',
          maxWidth: '460px',
          background: '#ffffff',
          borderRadius: '20px',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.3)',
          padding: '40px 32px',
          textAlign: 'center',
          animation: 'updateSlideIn 500ms ease-out',
        }}>
          {/* Update icon */}
          <div style={{
            width: '72px',
            height: '72px',
            margin: '0 auto 24px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            animation: 'updatePulse 2s ease-in-out infinite',
          }}>
            <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          </div>

          <h2 style={{
            margin: '0 0 12px',
            fontSize: '22px',
            fontWeight: '700',
            color: '#0f172a',
          }}>
            {t('updateBanner.forceTitle')}
          </h2>

          <div style={{
            padding: '16px 20px',
            background: '#f8fafc',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            marginBottom: '20px',
          }}>
            <p style={{
              margin: 0,
              fontSize: '15px',
              lineHeight: '1.6',
              color: '#334155',
            }}>
              {updateMessage || t('updateBanner.forceMessage')}
            </p>
          </div>

          {latestVersion && (
            <p style={{ margin: '0 0 20px', fontSize: '13px', color: '#94a3b8' }}>
              {t('updateBanner.newVersion', { version: latestVersion })}
            </p>
          )}

          {storeUrl && (
            <button
              onClick={handleUpdate}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
                padding: '14px 32px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                borderRadius: '12px',
                color: 'white',
                fontSize: '16px',
                fontWeight: '600',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line x1="12" y1="15" x2="12" y2="3" />
              </svg>
              {t('updateBanner.updateNow')}
            </button>
          )}
        </div>

        <style>{`
          @keyframes updateFadeIn {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          @keyframes updateSlideIn {
            from { transform: translateY(20px) scale(0.97); opacity: 0; }
            to   { transform: translateY(0) scale(1); opacity: 1; }
          }
          @keyframes updatePulse {
            0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(102, 126, 234, 0.4); }
            50%      { transform: scale(1.05); box-shadow: 0 0 0 12px rgba(102, 126, 234, 0); }
          }
        `}</style>
      </div>
    );

    return createPortal(overlay, document.body);
  }

  // Soft update → dismissible banner at top of dashboard-content
  return (
    <div style={{
      position: 'sticky',
      top: 0,
      left: 0,
      right: 0,
      zIndex: 99,
      margin: '-32px -32px 16px -32px',
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      padding: '12px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
      fontSize: '14px',
      fontWeight: '500',
      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      animation: 'bannerSlideDown 300ms ease-out',
    }}>
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="7 10 12 15 17 10" />
        <line x1="12" y1="15" x2="12" y2="3" />
      </svg>

      <span style={{ flex: 1, textAlign: 'center' }}>
        {updateMessage || t('updateBanner.softMessage', { version: latestVersion })}
      </span>

      {storeUrl && (
        <button
          onClick={handleUpdate}
          style={{
            padding: '6px 16px',
            background: 'rgba(255,255,255,0.2)',
            color: 'white',
            border: '1px solid rgba(255,255,255,0.4)',
            borderRadius: '6px',
            fontSize: '13px',
            fontWeight: '600',
            cursor: 'pointer',
            flexShrink: 0,
          }}
        >
          {t('updateBanner.update')}
        </button>
      )}

      <button
        onClick={onDismiss}
        style={{
          background: 'none',
          border: 'none',
          color: 'white',
          cursor: 'pointer',
          padding: '4px',
          display: 'flex',
          flexShrink: 0,
          opacity: 0.8,
        }}
        title={t('common.close')}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      <style>{`
        @keyframes bannerSlideDown {
          from { transform: translateY(-100%); }
          to   { transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default UpdateBanner;
